import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Trophy, Star, Target, Users, Building2, Beer, HeartHandshake,
  ChevronDown, ArrowRight, Zap, Rocket, Send, Sparkles, Crown,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";

const heroBg = "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBkYXJrJTIwbmlnaHR8ZW58MHx8fHwxNzgyMDUyNzI2fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Target, title: "Exact scores", desc: "Predict every fixture. Every score. Every week." },
  { icon: Star, title: "Weekly Joker", desc: "Play a Joker on one fixture to double the points." },
  { icon: Trophy, title: "Live leaderboards", desc: "Weekly, monthly and season tables update instantly." },
  { icon: Zap, title: "Automatic weekly lock", desc: "Pick your league's lock day and time — predictions lock automatically. No chasing your mates." },
];

const howItWorks = [
  { n: "01", icon: Rocket, title: "Create your league", desc: "Set up your competition in minutes." },
  { n: "02", icon: Send, title: "Invite your players", desc: "Share your unique league code." },
  { n: "03", icon: Target, title: "Make your picks", desc: "Predict each score and choose your weekly Joker." },
  { n: "04", icon: Crown, title: "Top the table", desc: "Scores and leaderboards update automatically." },
];

const orgTypes = [
  { icon: Building2, label: "Workplaces" },
  { icon: Beer, label: "Pubs & clubs" },
  { icon: Users, label: "Supporter groups" },
  { icon: HeartHandshake, label: "Groups of friends" },
];

const testimonials = [
  { name: "Dave M.", role: "The Crown, Manchester", quote: "Replaced our spreadsheet in ten minutes. The lads argue about the Joker for days." },
  { name: "Sarah K.", role: "HR Lead, Logix Ltd", quote: "Our office league is the best thing we've done for team culture. Zero admin from me." },
  { name: "Tom R.", role: "Supporters Club Secretary", quote: "Looks properly professional. Onboarding sixty members was painless." },
];

const faqs = [
  { q: "How does scoring work?", a: "Exact score = 5 points. Correct result only = 2 points. Wrong result = 0 points. Your Joker doubles the points from the fixture you nominate that week." },
  { q: "When do predictions lock?", a: "Automatically at your league's weekly lock time — Friday 6pm UK by default, and the league admin can choose any day and time. After the lock, only the competition admin can enter or edit a prediction on a player's behalf — perfect for offline members." },
  { q: "Can I run this as a workplace competition?", a: "Absolutely — Top of the Lot was built with workplaces in mind. Create a private league, invite your team, and every matchday becomes a conversation." },
  { q: "Do I have to be technical?", a: "Not at all. Set up a league, upload fixtures via CSV or add them one by one, and let the platform do the maths." },
  { q: "What happens on mobile?", a: "The whole app is mobile-first. Score inputs are big enough for a Sunday-morning thumb, and predictions are two taps away." },
];

const scoringExamples = [
  { pred: "Arsenal 2 – 1 Tottenham", result: "Arsenal 2 – 1 Tottenham", label: "Exact score", pts: 5, jokerPts: 10, tone: "success" },
  { pred: "Arsenal 3 – 1 Tottenham", result: "Arsenal 2 – 1 Tottenham", label: "Correct result", pts: 2, jokerPts: 4, tone: "warn" },
  { pred: "Arsenal 1 – 2 Tottenham", result: "Arsenal 2 – 1 Tottenham", label: "Wrong result", pts: 0, jokerPts: 0, tone: "muted" },
];

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-[#071426] text-white">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#071426]/75 border-b border-[#1F3A5C]">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <BrandMark />
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#AAB7C7]">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#scoring" className="hover:text-white transition-colors">Scoring</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="px-3 py-2 text-sm text-[#AAB7C7] hover:text-white" data-testid="landing-login">Login</Link>
            <Link to="/register" className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] text-sm font-bold uppercase tracking-wide transition-colors" data-testid="landing-cta-signup">
              Create a league
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden hero-grid">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#071426]/60 via-[#071426]/90 to-[#071426]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C7F500]/10 border border-[#C7F500]/30 text-[#C7F500] text-xs font-bold tracking-wider mb-6">
                <Sparkles className="w-3.5 h-3.5" /> FRIENDLY RIVALRY. SERIOUS BRAGGING RIGHTS.
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                THINK YOU KNOW <br className="hidden sm:block" />
                YOUR <span className="text-[#C7F500]">FOOTBALL?</span>
              </h1>
              <p className="mt-6 text-lg text-[#AAB7C7] max-w-xl leading-relaxed">
                Create a private football prediction league for your workplace, club or group. Predict the scores, play your weekly Joker and compete for serious bragging rights.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register" data-testid="hero-start-trial" className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] font-bold uppercase tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(199,245,0,0.5)]">
                  Create a league <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" data-testid="hero-view-demo" className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-[#132A45] hover:bg-[#1B4276] border border-[#1F3A5C] text-white font-bold uppercase tracking-wide transition-colors">
                  Join a league
                </Link>
              </div>
              <p className="mt-4 text-xs text-[#7A8AA3]">Demo: player@topofthelot.com / Player123!</p>
            </div>

            <div className="lg:col-span-5 fade-up">
              <div className="relative">
                <div className="absolute -inset-6 bg-[#C7F500]/8 blur-3xl rounded-full" />
                <div className="relative bg-[#0D2038]/90 backdrop-blur border border-[#1F3A5C] rounded-2xl p-5 shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#AAB7C7]">Gameweek 12 · Fri 18:00 lock</div>
                      <div className="font-heading text-lg tracking-wider mt-0.5">MAKE YOUR PICKS</div>
                    </div>
                    <Star className="w-5 h-5 text-[#F5B942] joker-pulse fill-[#F5B942]" />
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { h: "Arsenal", a: "Chelsea", joker: true, ph: 2, pa: 1 },
                      { h: "Liverpool", a: "Man City", joker: false, ph: 1, pa: 1 },
                      { h: "Newcastle", a: "Brighton", joker: false, ph: 3, pa: 0 },
                    ].map((m, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-xl border ${m.joker ? "border-[#F5B942]/50 joker-glow bg-[#F5B942]/5" : "border-[#1F3A5C]"} bg-[#071426]`}>
                        <div className="flex-1 text-right pr-3 font-medium truncate text-sm">{m.h}</div>
                        <div className="flex items-center gap-2">
                          <div className="w-11 h-10 rounded-lg bg-[#0D2038] border border-[#1F3A5C] flex items-center justify-center font-num font-bold">{m.ph}</div>
                          <span className="text-[#7A8AA3] text-xs">-</span>
                          <div className="w-11 h-10 rounded-lg bg-[#0D2038] border border-[#1F3A5C] flex items-center justify-center font-num font-bold">{m.pa}</div>
                        </div>
                        <div className="flex-1 pl-3 font-medium truncate text-sm">{m.a}</div>
                      </div>
                    ))}
                  </div>
                  <button className="mt-4 w-full py-3 rounded-xl bg-[#C7F500] hover:bg-[#D9FF27] font-heading tracking-wide text-[#071426]">
                    LOCK IN PREDICTIONS
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The pitch — no spreadsheets */}
      <section className="border-t border-[#1F3A5C] py-16">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <div className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-wide leading-tight">
            NO SPREADSHEETS.
            <span className="block text-[#AAB7C7]">NO CHASING PREDICTIONS.</span>
            <span className="block text-[#AAB7C7]">NO CALCULATING SCORES.</span>
          </div>
          <p className="mt-5 text-lg text-white">
            Top of the Lot handles <span className="text-[#C7F500] font-bold">everything</span>.
          </p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">What you get</p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-wide leading-tight">A COMPLETE PREDICTOR TOOLKIT</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="group p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] hover:border-[#C7F500]/40 hover:-translate-y-0.5 transition-all">
                  <div className="w-10 h-10 rounded-xl bg-[#C7F500]/10 text-[#C7F500] flex items-center justify-center mb-4 group-hover:bg-[#C7F500]/20 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-xl tracking-wide mb-1">{f.title}</h3>
                  <p className="text-sm text-[#AAB7C7] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">How it works</p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-wide leading-tight">FOUR STEPS TO THE TOP</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {howItWorks.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid={`how-step-${i + 1}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-[#132A45] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#C7F500]" />
                    </div>
                    <div className="font-num text-3xl text-[#1F3A5C] font-bold">{s.n}</div>
                  </div>
                  <h3 className="mt-6 font-heading text-lg tracking-wide">{s.title}</h3>
                  <p className="mt-1 text-sm text-[#AAB7C7]">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scoring explanation */}
      <section id="scoring" className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">Scoring</p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-wide leading-tight">SIMPLE. TRANSPARENT. RUTHLESS.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="score-exact">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#22C55E] font-bold">Exact score</div>
              <div className="font-num text-5xl font-bold mt-3">+5<span className="text-lg text-[#AAB7C7] font-medium ml-2">pts</span></div>
            </div>
            <div className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="score-result">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#AAB7C7] font-bold">Correct result</div>
              <div className="font-num text-5xl font-bold mt-3">+2<span className="text-lg text-[#AAB7C7] font-medium ml-2">pts</span></div>
            </div>
            <div className="p-6 rounded-2xl bg-[#0D2038] border border-[#F5B942]/40 relative overflow-hidden" data-testid="score-joker">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5B942] font-bold inline-flex items-center gap-1"><Star className="w-3 h-3 fill-[#F5B942]" /> Joker</div>
              <div className="font-num text-5xl font-bold mt-3 text-[#F5B942]">×2</div>
              <div className="mt-2 text-xs text-[#AAB7C7]">Doubles the points from your chosen fixture.</div>
            </div>
          </div>

          {/* Worked examples */}
          <div className="rounded-2xl bg-[#0D2038] border border-[#1F3A5C] p-6" data-testid="score-examples">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#AAB7C7] font-bold mb-4">Worked examples</div>
            <div className="space-y-3">
              {scoringExamples.map((ex, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center p-3 rounded-xl bg-[#071426] border border-[#1F3A5C]">
                  <div className="sm:col-span-5">
                    <div className="text-[10px] uppercase tracking-wider text-[#AAB7C7]">Prediction</div>
                    <div className="font-num font-semibold">{ex.pred}</div>
                  </div>
                  <div className="sm:col-span-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#AAB7C7]">Result</div>
                    <div className="font-num font-semibold">{ex.result}</div>
                  </div>
                  <div className="sm:col-span-3 text-right">
                    <span className={`text-xs uppercase tracking-wider font-bold ${ex.tone === "success" ? "text-[#22C55E]" : ex.tone === "warn" ? "text-[#C7F500]" : "text-[#EF4444]"}`}>
                      {ex.label}
                    </span>
                    <div className="font-num text-2xl font-bold">
                      {ex.pts} <span className="text-[#AAB7C7] text-sm font-medium">pts</span>
                    </div>
                    <div className="text-xs text-[#F5B942] font-num">
                      ★ Joker: {ex.jokerPts} pts
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">Pricing</p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-wide leading-tight">SIMPLE MONTHLY PLANS</h2>
            <p className="mt-4 text-[#AAB7C7] text-lg">Billed monthly in GBP. VAT & company billing available at checkout. Cancel any time.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { tier: "starter", name: "Starter", price: 9.99, cap: "Up to 25 players", tag: null, cta: "Start with Starter",
                perks: ["Weekly, monthly & season leaderboards", "Weekly Joker (×2 points)", "PDF exports for meetings", "Offline player management"] },
              { tier: "club", name: "Club", price: 19.99, cap: "Up to 120 players", tag: "MOST POPULAR", cta: "Choose Club",
                perks: ["Everything in Starter", "Custom monthly windows", "Live Premier League scores", "Priority admin experience"] },
              { tier: "pro", name: "Pro", price: 34.99, cap: "Up to 500 players", tag: null, cta: "Go Pro",
                perks: ["Everything in Club", "Multiple leagues", "Priority support", "Best for pubs & workplaces"] },
            ].map((p) => (
              <div key={p.tier} data-testid={`landing-plan-${p.tier}`}
                className={`p-6 rounded-2xl border relative flex flex-col ${p.tag ? "border-[#F5B942]/50 bg-[#0D2038]" : "border-[#1F3A5C] bg-[#0D2038]"}`}>
                {p.tag && (
                  <span className="absolute -top-3 left-6 text-[10px] font-bold px-2 py-1 rounded bg-[#F5B942] text-[#071426] tracking-wider">
                    {p.tag}
                  </span>
                )}
                <div className="font-heading text-2xl tracking-wide">{p.name}</div>
                <div className="text-xs text-[#AAB7C7] mt-1">{p.cap}</div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-num text-5xl font-bold">£{p.price.toFixed(2)}</span>
                  <span className="text-sm text-[#AAB7C7]">/ month</span>
                </div>
                <ul className="mt-6 space-y-2 flex-1">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm text-[#AAB7C7]">
                      <Sparkles className="w-3.5 h-3.5 text-[#C7F500] mt-0.5 shrink-0" />
                      {perk}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid={`landing-plan-cta-${p.tier}`}
                  className={`mt-6 inline-flex justify-center items-center gap-2 px-4 py-3 rounded-xl font-bold uppercase tracking-wide transition-colors ${
                    p.tag
                      ? "bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426]"
                      : "bg-[#132A45] hover:bg-[#1B4276] border border-[#1F3A5C] text-white"
                  }`}>
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-[#7A8AA3]">
            Every plan includes: unlimited fixtures & gameweeks · live Premier League scores · secure Stripe payments · no setup fees.
          </p>
        </div>
      </section>

      {/* Workplace positioning */}
      <section className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">For workplaces</p>
              <h2 className="font-heading text-3xl sm:text-5xl tracking-wide leading-tight">BRING SOME FRIENDLY RIVALRY TO WORK.</h2>
              <p className="mt-5 text-[#AAB7C7] text-lg leading-relaxed max-w-lg">
                Create a private prediction league for your team and turn every matchday into a talking point.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Team engagement", "Friendly competition", "Culture wins", "Zero admin"].map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#132A45] border border-[#1F3A5C] text-[#AAB7C7]">
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/register" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] font-bold uppercase tracking-wide">
                  Start a workplace league <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {orgTypes.map((o, i) => {
                const Icon = o.icon;
                return (
                  <div key={i} className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] hover:border-[#C7F500]/40 transition-all">
                    <Icon className="w-6 h-6 text-[#C7F500]" />
                    <div className="mt-4 font-heading text-lg tracking-wide">{o.label}</div>
                    <div className="text-xs text-[#AAB7C7] mt-1">Perfect fit</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="font-heading text-3xl sm:text-5xl tracking-wide mb-10">LOVED BY LEAGUE RUNNERS</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <figure key={i} className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
                <blockquote className="text-white leading-relaxed">"{t.quote}"</blockquote>
                <figcaption className="mt-4 text-sm text-[#AAB7C7]">
                  <span className="font-semibold text-white">{t.name}</span> · {t.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 border-t border-[#1F3A5C]">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="font-heading text-3xl sm:text-5xl tracking-wide mb-8 text-center">FREQUENTLY ASKED</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl bg-[#0D2038] border border-[#1F3A5C] overflow-hidden">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#132A45] transition-colors"
                >
                  <span className="font-medium">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 transition-transform text-[#AAB7C7] ${openFaq === i ? "rotate-180 text-[#C7F500]" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-[#AAB7C7] text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 border-t border-[#1F3A5C]">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-heading text-3xl sm:text-5xl tracking-wide">READY TO TOP THE TABLE?</h2>
          <p className="mt-4 text-[#AAB7C7]">Predict the scores. Play your Joker. Top the table.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] font-bold uppercase tracking-wide">
              Create a league <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-[#132A45] hover:bg-[#1B4276] border border-[#1F3A5C] text-white font-bold uppercase tracking-wide">
              Join a league
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1F3A5C] py-10">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#AAB7C7]">
          <BrandMark asLink={false} />
          <div className="flex gap-6">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="#scoring" className="hover:text-white">Scoring</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} Top of the Lot</div>
        </div>
      </footer>
    </div>
  );
}
